import { useApp } from '../context/AppContext';
import { api } from '../lib/api';
import { useEffect, useState } from 'react';

function fmtTime(iso?: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const TYPE_LABEL: Record<string, string> = {
  SONG_STATUS: 'Song Status',
  BATTLE_UPDATE: 'Battle Update',
  SYSTEM: 'System',
  ANNOUNCEMENT: 'Announcement',
  WAVE_REQUEST: 'Wave Request',
  MESSAGE: 'Message',
  TIER_CHANGE: 'Contract Change',
};

const TYPE_ICON: Record<string, string> = {
  SONG_STATUS: '🎵',
  BATTLE_UPDATE: '⚔',
  SYSTEM: '⚙',
  ANNOUNCEMENT: '📢',
  WAVE_REQUEST: '🌊',
  MESSAGE: '💬',
  TIER_CHANGE: '★',
};

export function NotificationsPage() {
  const { notifications, markNotificationRead, markAllRead, unreadCount } = useApp();
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [filter, setFilter] = useState<string>('all');

  // Load announcements (visible to all users)
  useEffect(() => {
    api.announcements().then((d) => setAnnouncements(d.announcements || [])).catch(() => {});
  }, []);

  const filtered = filter === 'all'
    ? notifications
    : notifications.filter(n => n.type === filter);

  const filterOptions = ['all', ...new Set(notifications.map(n => n.type).filter(Boolean))];

  return (
    <div className="grid">
      <div className="card">
        <div className="flex-between gap-8">
          <div>
            <div className="section-title" style={{ margin: 0 }}>Notifications</div>
            <div className="muted" style={{ fontSize: 13 }}>
              {unreadCount} unread of {notifications.length}
            </div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={markAllRead} disabled={unreadCount === 0}>
            Mark all read
          </button>
        </div>

        {filterOptions.length > 2 && (
          <div className="flex gap-4 wrap" style={{ marginTop: 12 }}>
            {filterOptions.map(f => (
              <button
                key={f}
                className={`btn btn-sm ${filter === f ? 'btn-gold' : 'btn-ghost'}`}
                onClick={() => setFilter(f)}
                style={{ fontSize: 12 }}
              >
                {f === 'all' ? 'All' : TYPE_LABEL[f] || f}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Announcements section */}
      {announcements.length > 0 && (
        <div className="card" style={{ borderColor: 'rgba(201,162,39,0.35)' }}>
          <div className="section-title gold" style={{ fontSize: 15 }}>📢 Announcements</div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
            Platform-wide announcements from IAMMUZIK administration.
          </div>
          {announcements.map((a) => (
            <div key={a.id} className="card" style={{ background: 'var(--iam-gray-2)', marginBottom: 8 }}>
              <div className="flex-between gap-8 mb-8">
                <strong style={{ fontSize: 15 }}>{a.title}</strong>
                <span className="muted" style={{ fontSize: 11 }}>{fmtTime(a.createdAt)}</span>
              </div>
              <p className="muted" style={{ fontSize: 14, margin: 0 }}>{a.body}</p>
              {a.audience !== 'all' && (
                <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                  Audience: {a.audience}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Notifications list */}
      {filtered.length === 0 ? (
        <div className="empty">No notifications{filter !== 'all' ? ` of type "${TYPE_LABEL[filter] || filter}"` : ''} yet.</div>
      ) : (
        filtered.map((n) => (
          <div key={n.id} className="card" style={{ background: n.read ? 'var(--iam-gray)' : 'var(--iam-gray-2)' }}>
            <div className="flex-between gap-8 mb-8">
              <div className="flex gap-8" style={{ alignItems: 'center' }}>
                <span style={{ fontSize: 18 }}>{n.type ? TYPE_ICON[n.type] || '🔔' : '🔔'}</span>
                <strong>{n.title}</strong>
              </div>
              <div className="flex gap-8" style={{ alignItems: 'center' }}>
                {n.type && (
                  <span className="chip" style={{ fontSize: 11 }}>{TYPE_LABEL[n.type] || n.type}</span>
                )}
                {!n.read && <span className="chip chip-requested">New</span>}
              </div>
            </div>
            <p className="muted" style={{ fontSize: 14, margin: 0 }}>{n.body}</p>
            <div className="flex-between" style={{ marginTop: 8 }}>
              <span className="muted" style={{ fontSize: 11 }}>{fmtTime(n.createdAt)}</span>
              {!n.read && (
                <button className="btn btn-ghost btn-sm" onClick={() => markNotificationRead(n.id)}>
                  Mark read
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
